<!-- /**
 * @file index.vue
 * @description 欺诈通知页面入口组件，整合搜索和数据表格功能
 * @module views/risk/fraud-notification/index
 * @Business 欺诈通知查询页面，展示欺诈通知数据，包括通知ID、通知时间、欺诈类型、交易流水号等信息
 * @Interaction 页面交互流程：
 *  1. 初始化时从URL获取查询参数，若无参数则使用默认日期范围（最近7天）
 *  2. SearchForm组件负责更新URL查询参数和处理导出功能
 *  3. DataTable组件监听URL参数变化并触发查询方法获取数据
 *  4. 用户可通过SearchForm筛选各种条件，筛选条件变化时自动更新表格数据
 *  5. 表格数据支持点击交易流水号跳转到交易详情页面
 */ -->
<template>
  <!-- 应用内容包装器 - 整体页面容器 -->
  <div class="app-content-wrapper">
    <common-info-description :customStyle="{ marginBottom: '24px' }"
      :text="$t('message.fraudNotification.fraudNotificationTips')" />
    <!-- 搜索组件包装器 - 包含条件筛选表单 -->
    <div class="search-wrapper">
      <search-form ref="searchForm" :on-export-click="onExportClick" />
    </div>
    <!-- 表格包装器 - 包含数据展示表格 -->
    <div class="table-wrapper">
      <data-table ref="dataTable" :service-function="queryList" />
    </div>
  </div>
</template>

<script>
// 导入所需的组件和工具
import CommonInfoDescription from '@/components/common/common-info-description/index.vue'; // 导入信息描述组件
import { initExhibitionTable } from '@/utils/tools/constant'; // 导入表格初始化工具，用于格式化API返回数据

import FraudNotificationAPI from './api'; // 导入欺诈通知服务API，提供数据请求方法
import DataTable from './components/DataTable.vue'; // 导入数据表格组件
import SearchForm from './components/SearchForm.vue'; // 导入搜索表单组件

/**
 * @vue-component FraudNotification
 * @description 欺诈通知主组件，协调搜索和数据表格之间的交互
 * @description Fraud notification main component, coordinating interactions between search and data table
 */
export default {
  name: 'FraudNotification', // 组件名称 / Component name

  components: {
    SearchForm, // 注册搜索表单组件 / Register search form component
    DataTable, // 注册数据表格组件 / Register data table component
    CommonInfoDescription, // 注册信息描述组件
  },

  data() {
    return {
    }; // 无需局部状态数据 / No local state data needed
  },

  methods: {
    /**
     * @method queryList
     * @async
     * @description 查询欺诈通知列表数据，作为服务函数传递给数据表格组件
     * @description Query fraud notification list data, passed as service function to data table component
     * @param {Object} params - 查询参数，来自URL或默认值 / Query parameters, from URL or default values
     * @returns {Promise<Object>} 格式化后的表格数据 / Formatted table data
     * @throws {Error} 请求失败时抛出错误 / Throws error on request failure
     */
    async queryList(params) {
      try {
        // 调用API获取欺诈通知列表数据 / Call API to get fraud notification list data
        const list = await FraudNotificationAPI.queryFraudNotificationList(params);
        // console.log('---list---', list, initExhibitionTable(list, true));

        // 格式化API返回数据为表格可用格式 / Format API return data to table usable format
        return list; // initExhibitionTable(list, true);
      } catch (error) {
        // 发生错误时返回rejected Promise / Return rejected Promise on error
        return Promise.reject(error);
      }
    },

    /**
     * @method onExportClick
     * @description 导出按钮点击处理函数
     * @description Export button click handler
     * @param {Object} params - 搜索参数 / Search parameters
     */
    async onExportClick(params) {
      try {
        // 调用API导出数据 / Call API to export data
        await FraudNotificationAPI.exportFraudNotification(params);
        this.$message.success(this.$t('message.common.downloadTask'));
      } catch (error) {
        console.error('导出失败 / Export failed:', error);
        this.$message.error(this.$t('message.common.exportFailed'));
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/variables.scss'; // 导入全局样式变量 / Import global style variables

.app-content-wrapper {

  // 页面容器样式 / Page container style
  .search-wrapper {
    // 搜索区域样式 / Search area style
    margin-bottom: 16px; // 底部外边距，与表格保持间距 / Bottom margin, to keep distance with table
  }
}
</style>
